import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import apiService from '../../services/apiService';
import { useToast } from '../ToastProvider';

// #649: Sperrliste für Auto-Antworten im Reiter „Postfach-Überwachung“.
// Eine Liste je Installation, gilt für alle überwachten Postfächer. An diese
// Adressen geht nie eine automatische Antwort, die Mail wird aber weiter
// verarbeitet (Beschluss 05.11.2026). Prüfung und Regel: server/utils/autoAntwort.js.
//
// Der Zustand lebt im Hook, den useMailboxTab (also das Panel) aufruft: Ein
// ungespeicherter Entwurf übersteht so den Reiterwechsel wie die Vorlagen-Entwürfe.
export function useAutoAntwortSperrliste() {
  const [gespeichert, setGespeichert] = useState(null); // null = noch nicht geladen
  const [entwurf, setEntwurf] = useState('');
  const [ladefehler, setLadefehler] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [ungueltig, setUngueltig] = useState([]);

  const laden = async () => {
    setLadefehler(false);
    try {
      const antwort = await apiService.getAutoReplyBlocklist();
      const liste = antwort.eintraege || [];
      setGespeichert(liste);
      setEntwurf(liste.join('\n'));
      setUngueltig([]);
    } catch (e) {
      console.error('Sperrliste laden fehlgeschlagen:', e);
      setLadefehler(true);
    }
  };

  return { gespeichert, entwurf, setEntwurf, ladefehler, speichert, setSpeichert, ungueltig, setUngueltig, setGespeichert, laden };
}

const AutoAntwortSperrliste = ({ sperrliste }) => {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const { gespeichert, entwurf, setEntwurf, ladefehler, speichert, setSpeichert, ungueltig, setUngueltig, setGespeichert, laden } = sperrliste;

  // Einmal laden, wenn der Reiter das erste Mal erscheint
  useEffect(() => {
    if (gespeichert === null && !ladefehler) laden();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const geaendert = gespeichert !== null && entwurf !== gespeichert.join('\n');

  const speichern = async () => {
    setSpeichert(true);
    setUngueltig([]);
    try {
      const antwort = await apiService.saveAutoReplyBlocklist(entwurf);
      const liste = antwort.eintraege || [];
      setGespeichert(liste);
      setEntwurf(liste.join('\n'));
      pushToast({ message: t('admin.blocklist.saved', 'Sperrliste gespeichert.'), type: 'success' });
    } catch (e) {
      const daten = e?.response?.data;
      if (daten?.code === 'invalidBlocklist' && Array.isArray(daten.ungueltig) && daten.ungueltig.length > 0) {
        setUngueltig(daten.ungueltig);
      } else {
        pushToast({ message: daten?.error || t('admin.blocklist.saveError', 'Sperrliste konnte nicht gespeichert werden.'), type: 'error' });
      }
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="admin-subsection auto-antwort-sperrliste">
      <h4>{t('admin.blocklist.heading', 'Sperrliste für Auto-Antworten')}</h4>
      <div className="settings-description">
        {t('admin.blocklist.desc', 'An diese Absender geht nie eine automatische Antwort, aus keinem überwachten Postfach. Ihre Mails werden trotzdem verarbeitet (Ticket bzw. Zuordnung). Gedacht als Notbremse, falls sich ein anderes System mit uns im Kreis antwortet.')}
      </div>
      {ladefehler ? (
        <div className="error-message">
          {t('admin.blocklist.loadError', 'Sperrliste konnte nicht geladen werden.')}{' '}
          <button type="button" className="btn btn-secondary btn-sm" onClick={laden}>
            {t('common.retry', 'Erneut versuchen')}
          </button>
        </div>
      ) : (
        <>
          <div className="form-group">
            <label htmlFor="auto-antwort-sperrliste">{t('admin.blocklist.label', 'Ein Eintrag je Zeile: Adresse oder ganze Domäne')}</label>
            <textarea
              id="auto-antwort-sperrliste"
              className="form-textarea"
              rows={5}
              value={entwurf}
              onChange={(e) => { setEntwurf(e.target.value); setUngueltig([]); }}
              placeholder={'ticket@example.com\n*@example.org'}
              disabled={gespeichert === null || speichert}
              aria-invalid={ungueltig.length > 0}
              aria-describedby={ungueltig.length > 0 ? 'auto-antwort-sperrliste-fehler' : undefined}
            />
            {ungueltig.length > 0 && (
              <div id="auto-antwort-sperrliste-fehler" className="field-error" role="alert">
                {t('admin.blocklist.invalid', 'Ungültige Einträge: {{eintraege}}', { eintraege: ungueltig.join(', ') })}
              </div>
            )}
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={speichern} disabled={!geaendert || speichert}>
              <Save size={16} />
              {speichert ? t('common.saving', 'Speichere...') : t('common.save', 'Speichern')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AutoAntwortSperrliste;
